import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import useProgram from "../../../utils/hooks/programs/useProgram";
import RichTextRenderer from "../../common/RichTextRenderer";
import ComIcon from "../../../assets/icons/com_icon.svg";
import moment from "moment";
import { getProgramOverview } from "../../../utils/api/endpoints/programs/programFullview";
import { ProgramType } from "../../../utils/api/endpoints/programs/parsePrograms";
import { isProgramDraft, isScopeSnapshotPending, isScopePendingVerification } from "../../../utils/programs/statusUtils";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import ProgramFeedbackRating from "./ProgramFeedbackRating";
import { useGetFeedbackStatusQuery } from "../../../utils/api/endpoints/program-feedback/programFeedbackApi";

interface ProgramOverview {
  id: number;
  status: string;
  totalReports: number;
  createdAt: string;
  testingStatus: string;
  expected_start_date?: string;
  type?: ProgramType;
}



interface ProgramHighlightProps {
  isHamburgerOpen?: boolean;
}

const ProgramHighlight: React.FC<ProgramHighlightProps> = ({ isHamburgerOpen = false }) => {
  const { id } = useParams();
  const { role } = useUserCredentials();
  const isAdminRole = role && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role as UserRole);
  const navigate = useNavigate();

  const { program } = useProgram(id ? parseInt(id) : undefined);

  const statusColors: Record<string, string> = {
    Active: "border-blue-500 border bg-blue-600 text-white",
    Draft: "border-amber-500 border bg-amber-500 text-white",
    Inactive: "border-gray-700 border bg-gray-200 bg-opacity-60 text-black",
  };

  const testingStatusColors: Record<string, string> = {
    Upcoming: "border-blue-500 bg-blue-300 text-black",
    Ongoing: "border-yellow-500 bg-orange-100 text-black",
    Completed: "border-green-700 bg-green-300 text-black",
  };

  const [programOverview, setProgramOverview] = useState<ProgramOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Get feedback status for business and admin users (not researchers)
  const { data: feedbackStatus } = useGetFeedbackStatusQuery(
    { programId: id ? parseInt(id) : 0 }, 
    { skip: !id || role === UserRole.RESEARCHER || role === UserRole.QA }
  );

  useEffect(() => {
    if (id) {
      const fetchProgramOverview = async () => {
        try {
          const data = await getProgramOverview(parseInt(id));
          setProgramOverview({
            ...data.program,
            type: data.program.type as ProgramType,
          });
        } catch (err) {
          setError("Failed to fetch program overview.");
        } finally {
          setLoadingOverview(false);
        }
      };
      fetchProgramOverview();
    }
  }, [id]);

  const truncateText = (text: string, wordLimit: number): string => {
    const words = text.split(/\s+/);
    return words.length > wordLimit ? words.slice(0, wordLimit).join(" ") + "..." : text;
  };

  return (
    <>
      {/* Wrapper to measure the container height */}
      <div className="program-highlight-container">
      {/* Program highlights card */}
      <section className="bg-white p-5 rounded-lg shadow mb-4">
        <h2 className="text-[18px] font-medium text-black">Program highlights</h2>
        <div className="h-[0.3px] bg-[#58CC02] mt-2 mb-4" />
        <div className="flex items-center justify-between">
          <div className="flex-1 pr-4">
            <h3 className="text-[16px] font-medium text-black capitalize">
              {program?.title || "No program available"}
            </h3>
            {program?.description && program.description.trim() !== "" && program.description.trim() !== "<p></p>" && (
              <>
                <div className="text-[#494949] mt-4 text-sm leading-relaxed">
                  <RichTextRenderer 
                    content={isExpanded ? program.description : truncateText(program.description, 55)} 
                  />
                </div>
                {program.description.split(/\s+/).length > 55 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-blue-600 text-sm font-semibold mt-1"
                  >
                    {isExpanded ? "Show Less" : "Read More"}
                  </button>
                )}
              </>
            )}

            {/* Feedback Rating - Show below description */}
            {feedbackStatus?.has_submitted && feedbackStatus.feedback?.overall_program_rating && feedbackStatus.feedback?.run_id == null && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <ProgramFeedbackRating
                  rating={feedbackStatus.feedback.overall_program_rating}
                  submittedAt={feedbackStatus.feedback.createdAt}
                  compact={true}
                />
              </div>
            )}
          </div>
          <div className="w-16 h-16 flex items-center justify-center rounded-lg ml-6">
            {program?.profilePicture ? (
              <img src={program.profilePicture} alt="company" className="w-full h-full object-cover rounded-lg" />
            ) : (
              <img src={ComIcon} alt="company" className="w-16 h-16 rounded-xl" />
            )}
          </div>
        </div>
      </section>

      {/* KPI strip */}
      <section className="rounded-lg overflow-hidden mb-4">
        {loadingOverview ? (
          <div className="bg-white px-5 py-3 text-gray-500 text-sm">Loading program details...</div>
        ) : error ? (
          <div className="bg-white px-5 py-3 text-red-500 text-sm">{error}</div>
        ) : programOverview ? (
          (() => {
            const showStartDate = !(programOverview.type === ProgramType.PTAAS && (programOverview.expected_start_date === "Invalid date" || !programOverview.expected_start_date));
            const showTestingPeriod = programOverview.type === ProgramType.PTAAS && programOverview.testingStatus !== "N/A" && programOverview.expected_start_date !== "Invalid date" && programOverview.expected_start_date;
            
            let gridCols = "grid-cols-2";
            if (showStartDate && showTestingPeriod) {
              gridCols = programOverview.type === ProgramType.PTAAS ? "grid-cols-4" : "grid-cols-3";
            } else if (showStartDate || showTestingPeriod) {
              gridCols = "grid-cols-3";
            }

            return (
              <div className={`w-full grid ${gridCols} gap-0 bg-[#0B1734] text-white min-h-[165px] items-center`}> 
                {/* Status */}
                <div className="relative px-6 py-5">
                  <div className="text-[14px] font-normal text-[#FFFFFF]">Status</div>
                  <div className="mt-2 text-white">
                    <span className={`w-fit px-4 py-1.5 rounded text-sm font-semibold flex items-center gap-2 ${
                      isAdminRole && isScopePendingVerification(program)
                        ? "bg-blue-600 text-white"
                        : isScopeSnapshotPending(program)
                          ? "bg-red-600 text-white"
                          : isProgramDraft(program)
                            ? statusColors.Draft
                            : statusColors[programOverview.status] || "bg-gray-500 text-white"
                    }`}>
                      {(isAdminRole && isScopePendingVerification(program)) || isScopeSnapshotPending(program) ? (
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                        </span>
                      ) : null}
                      {isAdminRole && isScopePendingVerification(program)
                        ? "Pending Verification"
                        : isScopeSnapshotPending(program)
                          ? "Scope Snapshot: Pending"
                          : isProgramDraft(program)
                            ? "Draft"
                            : (programOverview.status || "Unknown")}
                    </span>
                  </div>
                  <div className="text-[11px] opacity-80 mt-2">
                    {moment(programOverview.createdAt).utc().format("DD MMM YYYY HH:mm:ss")} 
                  </div>
                  {/* Vertical divider with top/bottom margins */}
                  <div className="hidden md:block absolute right-0 top-4 bottom-4 w-[2px] bg-[#D2D1D1]" />
                </div>

                {/* Start Date */}
                {showStartDate && (
                  <div className="relative px-6 py-5">
                    <div className="text-[14px] font-normal text-[#FFFFFF]">Start Date</div>
                    <div className="mt-2 text-[16px] font-semibold">
                      {programOverview?.type === ProgramType.PTAAS ? programOverview.expected_start_date : programOverview.createdAt}
                    </div>
                    <div className="text-[11px] opacity-80 mt-2">Average time to first response</div>
                    <div className="hidden md:block absolute right-0 top-4 bottom-4 w-[2px] bg-[#D2D1D1]" />
                  </div>
                )}

                {/* Testing period (PTAAS only) */}
                {showTestingPeriod && (
                  <div className="relative px-6 py-5">
                    <div className="text-[14px] font-normal text-[#FFFFFF]">Testing period</div>
                    <div className="mt-2">
                      <span className={`px-4 py-1.5 rounded text-sm font-semibold inline-block ${
                        testingStatusColors[programOverview.testingStatus] || "border-gray-500 text-gray-900 bg-gray-100"
                      }`}>
                        {programOverview.testingStatus}
                      </span>
                    </div>
                    <div className="text-[11px] font-medium opacity-80 mt-2">Started at {programOverview.expected_start_date}</div>
                    <div className="hidden md:block absolute right-0 top-4 bottom-4 w-[2px] bg-[#D2D1D1]" />
                  </div>
                )}

                {/* Total Reports */}
                <div className="px-6 py-5">
                  <div className="text-[14px] font-normal text-[#FFFFFF]">Total Reports</div>
                  <div className="mt-2 text-[16px] font-semibold">
                    {programOverview.totalReports || 0}
                  </div>
                  <div className="text-[11px] opacity-80 mt-2">Average time to triage</div>
                </div>
              </div>
            );
          })()
        ) : null}
      </section>
      </div>
    </>
  );
};

export default ProgramHighlight;